// ===================================================================
// PA GRANTS — Cross-Project Insurance Records
// ===================================================================

import { useEffect, useState, useMemo } from 'react';
import {
  Title, Text, Paper, Group, Stack, Badge, Button, TextInput, Select,
  Modal, ActionIcon, Loader, Center, SimpleGrid, Divider,
} from '../../components/gab/GabComponents';
import { IconPlus, IconEdit, IconTrash, IconSearch, IconRefresh, IconShieldCheck } from '@tabler/icons-react';
import { usePAGrantsStore } from '../../store/paGrantsStore';
import { notifications } from '../../lib/notifications';

const STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'claim_filed', label: 'Claim Filed' },
  { value: 'under_review', label: 'Under Review' },
  { value: 'settled', label: 'Settled' },
  { value: 'denied', label: 'Denied' },
];

const STATUS_COLORS: Record<string, string> = {
  pending: '#939598', claim_filed: 'var(--color-primary-700)', under_review: '#C68700', settled: 'var(--color-success)', denied: 'var(--color-error)',
};

const COVERAGE_OPTIONS = [
  { value: 'flood', label: 'Flood' },
  { value: 'wind', label: 'Wind/Hurricane' },
  { value: 'general_liability', label: 'General Liability' },
  { value: 'property', label: 'Property' },
  { value: 'other', label: 'Other' },
];

const EMPTY_FORM = {
  carrier: '', policyNumber: '', coverageType: 'property',
  claimNumber: '', settlementAmount: '', deductible: '', femaDeduction: '',
  status: 'pending', notes: '',
};

function fmt(n: number) {
  return n > 0 ? `$${n.toLocaleString(undefined, { minimumFractionDigits: 0 })}` : '—';
}

export default function Insurance() {
  const {
    insurance, fetchInsurance, createInsurance, updateInsurance, deleteInsurance,
    projects, fetchProjects,
  } = usePAGrantsStore();

  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | null>(null);
  const [form, setForm] = useState({ ...EMPTY_FORM });
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []); // eslint-disable-line

  async function load() {
    setLoading(true);
    await Promise.all([fetchInsurance(), fetchProjects()]);
    setLoading(false);
  }

  const projectMap = useMemo(() => {
    const m: Record<number, string> = {};
    projects.forEach((p: any) => { m[p.Rid] = p.projectNumber || p.title || `Project ${p.Rid}`; });
    return m;
  }, [projects]);

  const filtered = useMemo(() => {
    let list = insurance as any[];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(r =>
        (r.carrier || '').toLowerCase().includes(q) ||
        (r.policyNumber || '').toLowerCase().includes(q) ||
        (r.claimNumber || '').toLowerCase().includes(q)
      );
    }
    if (statusFilter) list = list.filter(r => r.status === statusFilter);
    return list;
  }, [insurance, search, statusFilter]);

  function openAdd() {
    setEditingId(null);
    setForm({ ...EMPTY_FORM });
    setModalOpen(true);
  }

  function openEdit(r: any) {
    setEditingId(r.Rid);
    setForm({
      carrier: r.carrier || '', policyNumber: r.policyNumber || '',
      coverageType: r.coverageType || 'property', claimNumber: r.claimNumber || '',
      settlementAmount: String(r.settlementAmount || ''), deductible: String(r.deductible || ''),
      femaDeduction: String(r.femaDeduction || ''), status: r.status || 'pending',
      notes: r.notes || '',
    });
    setModalOpen(true);
  }

  async function handleSave() {
    if (!form.carrier) { notifications.show({ message: 'Carrier required', color: 'var(--color-error)' }); return; }
    setSaving(true);
    try {
      const data = {
        carrier: form.carrier, policyNumber: form.policyNumber,
        coverageType: form.coverageType, claimNumber: form.claimNumber,
        settlementAmount: Number(form.settlementAmount) || 0,
        deductible: Number(form.deductible) || 0,
        femaDeduction: Number(form.femaDeduction) || 0,
        status: form.status, notes: form.notes,
      };
      if (editingId) {
        await updateInsurance(editingId, data);
      } else {
        await createInsurance(data);
      }
      notifications.show({ message: editingId ? 'Updated' : 'Added', color: 'var(--color-success)' });
      setModalOpen(false);
    } catch {
      notifications.show({ message: 'Failed to save', color: 'var(--color-error)' });
    }
    setSaving(false);
  }

  async function handleDelete(r: any) {
    if (!confirm(`Delete policy ${r.policyNumber || r.carrier}?`)) return;
    await deleteInsurance(r.Rid);
    notifications.show({ message: 'Deleted', color: '#939598' });
  }

  const totalSettled = filtered.reduce((s: number, r: any) => s + (Number(r.settlementAmount) || 0), 0);
  const totalFema = filtered.reduce((s: number, r: any) => s + (Number(r.femaDeduction) || 0), 0);

  return (
    <Stack gap="md">
      <Group justify="space-between">
        <Title order={3} c="black">Insurance Records</Title>
        <Button variant="primary" size="sm" leftIcon={<IconPlus size={14} />} onClick={openAdd}>
          Add Record
        </Button>
      </Group>

      {/* Summary bar */}
      {filtered.length > 0 && (
        <SimpleGrid cols={{ base: 1, sm: 3 }} spacing="sm">
          {[
            { label: 'Total Records', value: filtered.length.toString() },
            { label: 'Total Settlements', value: fmt(totalSettled) },
            { label: 'FEMA Deductions', value: fmt(totalFema), color: totalFema > 0 ? 'var(--color-error)' : undefined },
          ].map(s => (
            <Paper key={s.label} p="sm" radius="md" withBorder>
              <Text size="xs" c="dimmed" tt="uppercase" fw={600}>{s.label}</Text>
              <Text size="lg" fw={700} c={s.color}>{s.value}</Text>
            </Paper>
          ))}
        </SimpleGrid>
      )}

      <Paper p="md" radius="md" withBorder>
        <Group gap="sm" mb="md">
          <TextInput
            placeholder="Search carrier, policy, claim…"
            leftSection={<IconSearch size={14} />}
            value={search}
            onChange={e => setSearch(e.target.value)}
            style={{ flex: 1 }}
            size="sm"
          />
          <Select
            placeholder="All statuses"
            data={STATUS_OPTIONS}
            value={statusFilter}
            onChange={setStatusFilter}
            clearable size="sm"
            style={{ width: 160 }}
          />
          <ActionIcon variant="subtle" size="md" onClick={load} title="Refresh">
            <IconRefresh size={16} />
          </ActionIcon>
        </Group>

        {loading ? (
          <Center py="xl"><Loader /></Center>
        ) : filtered.length === 0 ? (
          <Stack align="center" py="xl" gap="sm">
            <IconShieldCheck size={40} color="var(--color-neutral-400)" />
            <Text c="dimmed">No insurance records found.</Text>
            <Button size="sm" variant="light" leftIcon={<IconPlus size={14} />} onClick={openAdd}>
              Add First Record
            </Button>
          </Stack>
        ) : (
          <Stack gap="sm">
            {filtered.map((r: any) => (
              <Paper key={r.Rid} p="md" withBorder>
                <Group justify="space-between" mb="sm">
                  <Group gap="sm">
                    <Text fw={600}>{r.carrier || 'Unknown Carrier'}</Text>
                    {r.policyNumber && <Text size="sm" c="dimmed">Policy: {r.policyNumber}</Text>}
                    {r.projectId && (
                      <Badge size="xs" variant="outline">{projectMap[r.projectId] || `#${r.projectId}`}</Badge>
                    )}
                  </Group>
                  <Group gap="xs">
                    <Badge variant="light" color={STATUS_COLORS[r.status] || '#939598'}>
                      {(r.status || 'pending').replace(/_/g, ' ')}
                    </Badge>
                    <ActionIcon variant="subtle" size="sm" onClick={() => openEdit(r)}>
                      <IconEdit size={14} />
                    </ActionIcon>
                    <ActionIcon variant="subtle" size="sm" color="var(--color-error)" onClick={() => handleDelete(r)}>
                      <IconTrash size={14} />
                    </ActionIcon>
                  </Group>
                </Group>

                <SimpleGrid cols={{ base: 2, md: 4 }} spacing="sm">
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">Coverage Type</Text>
                    <Text size="sm" fw={500}>{r.coverageType || '—'}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">Deductible</Text>
                    <Text size="sm" fw={500}>{fmt(Number(r.deductible) || 0)}</Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">Settlement Amount</Text>
                    <Text size="sm" fw={500} c={Number(r.settlementAmount) > 0 ? 'var(--color-success)' : undefined}>
                      {fmt(Number(r.settlementAmount) || 0)}
                    </Text>
                  </Stack>
                  <Stack gap={2}>
                    <Text size="xs" c="dimmed" tt="uppercase">FEMA Deduction</Text>
                    <Text size="sm" fw={500} c={Number(r.femaDeduction) > 0 ? 'var(--color-error)' : undefined}>
                      {fmt(Number(r.femaDeduction) || 0)}
                    </Text>
                  </Stack>
                </SimpleGrid>

                {r.claimNumber && (
                  <>
                    <Divider my="xs" />
                    <Text size="xs" c="dimmed">Claim: <Text span fw={500} size="xs">{r.claimNumber}</Text></Text>
                  </>
                )}
                {r.notes && <Text size="xs" c="dimmed" mt="xs">{r.notes}</Text>}
              </Paper>
            ))}
          </Stack>
        )}
      </Paper>

      <Modal opened={modalOpen} onClose={() => setModalOpen(false)} title={editingId ? 'Edit Insurance Record' : 'Add Insurance Record'}>
        <Stack gap="sm">
          <SimpleGrid cols={{ base: 1, sm: 2 }} spacing="sm">
            <TextInput label="Insurance Carrier" required value={form.carrier} onChange={e => setForm(f => ({ ...f, carrier: e.target.value }))} />
            <TextInput label="Policy Number" value={form.policyNumber} onChange={e => setForm(f => ({ ...f, policyNumber: e.target.value }))} />
            <Select label="Coverage Type" data={COVERAGE_OPTIONS} value={form.coverageType} onChange={v => setForm(f => ({ ...f, coverageType: v || 'property' }))} />
            <Select label="Status" data={STATUS_OPTIONS} value={form.status} onChange={v => setForm(f => ({ ...f, status: v || 'pending' }))} />
            <TextInput label="Claim Number" value={form.claimNumber} onChange={e => setForm(f => ({ ...f, claimNumber: e.target.value }))} />
            <TextInput label="Deductible ($)" type="number" value={form.deductible} onChange={e => setForm(f => ({ ...f, deductible: e.target.value }))} />
            <TextInput label="Settlement Amount ($)" type="number" value={form.settlementAmount} onChange={e => setForm(f => ({ ...f, settlementAmount: e.target.value }))} />
            <TextInput label="FEMA Deduction ($)" type="number" value={form.femaDeduction} onChange={e => setForm(f => ({ ...f, femaDeduction: e.target.value }))} />
          </SimpleGrid>
          <TextInput label="Notes" value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} />
          <Group justify="flex-end" gap="sm" mt="sm">
            <Button variant="ghost" onClick={() => setModalOpen(false)}>Cancel</Button>
            <Button variant="primary" loading={saving} onClick={handleSave}>Save</Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
